import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { mockPublicVacancy } from '@/mocks/public';

export default function PublicVacancyLanding() {
  const { slug } = useParams();
  const v = mockPublicVacancy;
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    telegram: '',
    whatsapp: '',
    preferred_contact: 'email',
    cover_letter: '',
    resume: null as File | null,
  });
  const [answers, setAnswers] = useState<Record<string, string>>();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, resume: file }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1500);
  };

  const formatSalary = (from?: number, to?: number) => {
    if (!from && !to) return '';
    const f = from ? `${from.toLocaleString('ru-RU')} ₽` : '';
    const t = to ? `${to.toLocaleString('ru-RU')} ₽` : '';
    if (f && t) return `${f} — ${t}`;
    return f || t;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-2xl text-emerald-600"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
            Заявка отправлена!
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed mb-6">
            Спасибо за интерес к позиции. Мы рассмотрим вашу кандидатуру и свяжемся с вами в ближайшее время.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-primary-700 hover:text-primary-800 transition-colors cursor-pointer"
          >
            ← Вернуться к вакансии
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Dark hero */}
      <div className="relative bg-foreground-950 text-white overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20geometric%20technology%20background%20with%20subtle%20glowing%20neural%20network%20lines%20and%20nodes%2C%20deep%20charcoal%20black%20with%20warm%20amber%20and%20emerald%20accent%20highlights%2C%20minimalist%20corporate%20tech%20aesthetic%2C%20soft%20gradient%20lighting%2C%20no%20text%2C%20clean%20modern%20illustration%20for%20SaaS%20platform%20hero%20section&width=1600&height=600&seq=public-hero-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60"></div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-16 md:py-24">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <span className="text-white font-heading font-bold text-sm">S</span>
            </div>
            <span className="text-sm text-white/60">{v.company_name}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold leading-tight mb-4 max-w-3xl">
            {v.vacancy_title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {v.salary_from && (
              <span className="text-lg md:text-xl font-heading font-semibold text-emerald-400">
                {formatSalary(v.salary_from, v.salary_to)}
              </span>
            )}
            <span className="text-sm text-white/50 flex items-center gap-1">
              <i className="ri-map-pin-line"></i>
              {v.location}
            </span>
          </div>
          <a
            href="#apply"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Откликнуться на вакансию
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left — description */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
              <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                О вакансии
              </h2>
              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed whitespace-pre-line">
                {v.description}
              </div>
            </div>

            {v.show_contacts_on_landing && (
              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
                <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                  Контакты
                </h2>
                <div className="flex flex-wrap gap-3">
                  {v.contact_email && (
                    <a
                      href={`mailto:${v.contact_email}`}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 transition-colors"
                    >
                      <i className="ri-mail-line text-primary-500"></i>
                      {v.contact_email}
                    </a>
                  )}
                  {v.contact_telegram && (
                    <span className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-700">
                      <i className="ri-telegram-line text-accent-500"></i>
                      {v.contact_telegram}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right — application form */}
          <div className="lg:col-span-1">
            <div id="apply" className="bg-white rounded-xl border border-background-200 p-5 md:p-6 sticky top-6">
              <h2 className="text-base font-heading font-semibold text-foreground-900 mb-4">
                Откликнуться
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Имя и фамилия <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Иван Иванов"
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Телефон
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="+7 (999) 000-00-00"
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                      Telegram
                    </label>
                    <input
                      type="text"
                      value={formData.telegram}
                      onChange={(e) => setFormData((prev) => ({ ...prev, telegram: e.target.value }))}
                      placeholder="@username"
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                      WhatsApp
                    </label>
                    <input
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData((prev) => ({ ...prev, whatsapp: e.target.value }))}
                      placeholder="+7..."
                      className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Предпочтительный способ связи
                  </label>
                  <select
                    value={formData.preferred_contact}
                    onChange={(e) => setFormData((prev) => ({ ...prev, preferred_contact: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Телефон</option>
                    <option value="telegram">Telegram</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Сопроводительное письмо
                  </label>
                  <textarea
                    value={formData.cover_letter}
                    onChange={(e) => setFormData((prev) => ({ ...prev, cover_letter: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                    placeholder="Кратко о себе и почему вы подходите на эту позицию..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    Резюме (PDF, до 10 МБ)
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-50 border border-background-200 border-dashed text-sm text-foreground-500 hover:border-primary-300 transition-colors">
                      <i className="ri-upload-cloud-line"></i>
                      {formData.resume ? formData.resume.name : 'Выберите или перетащите PDF'}
                    </div>
                  </div>
                </div>

                {/* Questions */}
                <div className="pt-2 border-t border-background-200">
                  <p className="text-xs font-medium text-foreground-600 mb-3">
                    Ответьте на вопросы анкеты (минимум 50 символов каждый)
                  </p>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {v.questions.map((q, i) => (
                      <div key={q.id}>
                        <label className="block text-xs text-foreground-500 mb-1">
                          {i + 1}. {q.text}
                        </label>
                        <textarea
                          value={answers[q.id] || ''}
                          onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                          placeholder="Ваш ответ..."
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 px-4 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Отправка...
                    </>
                  ) : (
                    <>
                      Отправить отклик
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-foreground-950 text-white/40 py-8 px-6">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center">
              <span className="text-white font-heading font-bold text-xs">S</span>
            </div>
            <span className="text-sm text-white/60">{v.company_name}</span>
          </div>
          <p className="text-xs">
            Работает на HR.Starec — AI-платформа подбора персонала
          </p>
        </div>
      </div>
    </div>
  );
}